import { Button, DialogV2, Loader, Typography } from '@recruit-robin/robin-theme/web-components';
import { Fragment, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { routes } from 'config/routes';
import { AccountStatus } from 'shared/legacy/contexts/CandidateContext/CandidateEmailSettingsContext.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { useOauthNylasStatusQuery } from 'shared/legacy/hooks/useOauthNylasStatusQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useIsGlobalDialogOpen } from 'stores/global-dialogs/hooks/useIsGlobalDialogOpen';
import { useStyles } from './ShouldConnectEmailDialog.styles';

enum EmailDialogState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error',
  INVALID = 'invalid',
}

export const ShouldConnectEmailDialog = memo(() => {
  const isVisible = useIsGlobalDialogOpen(GlobalDialogType.ShouldConnectEmail);
  const classes = useStyles();
  const [dialogState, setDialogState] = useState(EmailDialogState.INITIAL);
  const { close } = useGlobalDialog();
  const { dictionary } = useContext(LocalizationContext);
  const { currentUser } = useContext(UserContext);

  const { data: { email: emailStatus } = {}, isFetching: isLoadingEmailStatus, refetch } = useOauthNylasStatusQuery();

  useEffect(() => {
    if (emailStatus === AccountStatus.Invalid) {
      setDialogState(EmailDialogState.INVALID);
    }

    if (dialogState === EmailDialogState.CONNECTING && !isLoadingEmailStatus) {
      if (emailStatus === AccountStatus.Valid) {
        setDialogState(EmailDialogState.CONNECTED);
      } else {
        setDialogState(EmailDialogState.ERROR);
      }
    }
  }, [emailStatus, isLoadingEmailStatus, isVisible]);

  const verifyConnectionAfterFocus = useCallback(async () => {
    window.removeEventListener('focus', verifyConnectionAfterFocus);

    handleVerifyConnection();
  }, []);

  const handleClose = () => {
    setDialogState(EmailDialogState.INITIAL);
    close(GlobalDialogType.ShouldConnectEmail);
  };

  const handleConnect = () => {
    setDialogState(EmailDialogState.CONNECTING);

    window.open(`${window.location.origin}${routes.emailSettings}`, '_blank');

    window.addEventListener('focus', verifyConnectionAfterFocus);
  };

  const handleVerifyConnection = () => {
    setDialogState(EmailDialogState.CONNECTING);
    refetch();
  };

  const texts = useMemo(() => {
    switch (dialogState) {
      case EmailDialogState.INITIAL:
        return {
          title: dictionary.connectYourEmailToContinue,
          description: dictionary.shouldConnectEmailDialogDescription,
        };

      case EmailDialogState.CONNECTING:
        return {
          title: dictionary.verifyConnection,
          description: `${dictionary.pleaseWaitWhileWeVerifyYourEmailConnection}...`,
        };

      case EmailDialogState.CONNECTED:
        return {
          title: dictionary.emailConnected,
          description: dictionary.yourEmailIsConnectedAndReadyToUse,
        };

      case EmailDialogState.ERROR:
        return {
          title: dictionary.errorWhileConnectingEmail,
          description: dictionary.errorWhileConnectingEmailDescription,
        };

      case EmailDialogState.INVALID:
        return {
          title: dictionary.reconnectEmailAccount,
          description: dictionary.remindToReconnectEmail(currentUser?.first_name ?? ''),
        };
    }
  }, [dialogState]);

  const renderFooterButtons = useMemo(() => {
    switch (dialogState) {
      case EmailDialogState.INITIAL:
        return (
          <Fragment key={EmailDialogState.INITIAL}>
            <Button type="subtle1" style="text" onClick={handleClose} label={dictionary.cancel} />
            <Button type="highlight" style="filled" onClick={handleConnect} label={dictionary.connectEmailAccount} />
          </Fragment>
        );

      case EmailDialogState.CONNECTING:
        return (
          <Button
            key={EmailDialogState.CONNECTING}
            type="highlight"
            style="filled"
            label={dictionary.verifyingConnection}
            startIcon={<Loader size={16} />}
            onClick={() => {}}
          />
        );

      case EmailDialogState.CONNECTED:
        return (
          <Button
            key={EmailDialogState.CONNECTED}
            type="highlight"
            style="filled"
            onClick={handleClose}
            label={`${dictionary.ok}!`}
          />
        );

      case EmailDialogState.ERROR:
        return (
          <Fragment key={EmailDialogState.ERROR}>
            <Button type="subtle1" style="text" onClick={handleClose} label={dictionary.cancel} />
            <Button type="subtle2" style="text" onClick={handleVerifyConnection} label={dictionary.verifyConnection} />
            <Button type="highlight" style="filled" onClick={handleConnect} label={dictionary.tryAgain} />
          </Fragment>
        );

      case EmailDialogState.INVALID:
        return (
          <Fragment key={EmailDialogState.INVALID}>
            <Button type="subtle1" style="text" onClick={handleClose} label={dictionary.cancel} />
            <Button type="highlight" style="filled" onClick={handleConnect} label={dictionary.reconnectEmailAccount} />
          </Fragment>
        );
    }
  }, [dialogState]);

  return (
    <DialogV2.Root isVisible={isVisible} customCss={classes.root}>
      <DialogV2.Backdrop />

      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Header>
          <Typography variant={(v) => v.heading[2]}>{texts.title}</Typography>
        </DialogV2.Header>

        <DialogV2.Body>
          <Typography variant={(v) => v.body.long}>{texts.description}</Typography>
        </DialogV2.Body>

        <DialogV2.Footer>{renderFooterButtons}</DialogV2.Footer>
      </DialogV2.Content>
    </DialogV2.Root>
  );
});
